import {
  LayoutDashboard,
  Users,
  CreditCard,
  Languages,
  KeyRound,
  Plug,
  BookOpen,
  BrainCircuit,
  Code2,
  Store,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { serviceIcon } from '../../features/services/serviceIcons';
import type { ServiceGroup } from '../../features/services/types';

export type ProfileMenuItem = {
  id: string;
  labelKey: string;
  path: string;
  Icon: LucideIcon;
  feature?: string;
  locked?: boolean;
};

type ProfileMenuGroup = {
  id: string;
  labelKey: string;
  items: ProfileMenuItem[];
};

const profileMenuGroups: ProfileMenuGroup[] = [
  {
    id: 'workspace',
    labelKey: 'sidebar.menuGroups.workspace',
    items: [
      { id: 'dashboard', labelKey: 'sidebar.dashboard', path: '/dashboard', Icon: LayoutDashboard },
      { id: 'agents', labelKey: 'sidebar.agents', path: '/agents', Icon: Users },
      {
        id: 'characteristics',
        labelKey: 'sidebar.characteristics',
        path: '/characteristics',
        Icon: BrainCircuit,
      },
      { id: 'knowledge', labelKey: 'sidebar.knowledge', path: '/knowledge', Icon: BookOpen },
      { id: 'tools', labelKey: 'sidebar.tools', path: '/tools', Icon: Wrench },
    ],
  },
  {
    id: 'services',
    labelKey: 'sidebar.menuGroups.services',
    items: [
      {
        id: 'translate',
        labelKey: 'sidebar.multilanguage',
        path: '/services/translate',
        Icon: Languages,
      },
      { id: 'integrations', labelKey: 'sidebar.integrations', path: '/integrations', Icon: Plug },
      { id: 'marketplace', labelKey: 'sidebar.marketplace', path: '/marketplace', Icon: Store },
    ],
  },
  {
    id: 'developer',
    labelKey: 'sidebar.menuGroups.developer',
    items: [
      {
        id: 'api-keys',
        labelKey: 'sidebar.apiKeys',
        path: '/api-keys',
        Icon: KeyRound,
        feature: 'api_access',
      },
      {
        id: 'embed-widgets',
        labelKey: 'sidebar.embedWidgets',
        path: '/embed-widgets',
        Icon: Code2,
        feature: 'embed_widget',
      },
    ],
  },
  {
    id: 'account',
    labelKey: 'sidebar.menuGroups.account',
    items: [
      {
        id: 'subscription',
        labelKey: 'sidebar.subscription',
        path: '/subscription',
        Icon: CreditCard,
      },
    ],
  },
];

export const profileMenuGroupLabelClassName =
  'bg-transparent px-4 pb-1 pt-2 text-xs font-medium italic tracking-normal text-gray-400 dark:text-slate-500';

/** Capability-backed services (GET /v1/me/services) lead the static Services entries. */
export function withServiceGroups(
  serviceGroups: ServiceGroup[],
  isFeatureLocked: (code: string) => boolean
) {
  const dynamicItems: ProfileMenuItem[] = serviceGroups.map((group) => ({
    id: group.code,
    labelKey: group.labelKey,
    path: group.routePath,
    Icon: serviceIcon(group.icon),
    feature: group.code,
    locked: group.locked === true,
  }));
  return profileMenuGroups.map((group) =>
    group.id === 'services'
      ? { ...group, items: [...dynamicItems, ...group.items] }
      : {
          ...group,
          items: group.items.map((item) =>
            item.feature ? { ...item, locked: isFeatureLocked(item.feature) } : item
          ),
        }
  );
}
